import {useCallback} from 'react';
import {useDispatch} from 'react-redux';

import {useCachedTable} from 'src/hooks/common-data-service/cached-table';
import {searchSsoDrafts} from 'src/openapi/admin/apis/SsoDraftsApi';
import {useAppDispatch} from 'src/store/hooks';

import {buildSsoDraftSearchCriteria} from '../services/build-sso-draft-search-criteria';
import {ssoAccountsStateSelector} from '../services/sso-accounts-state.slice';
import {
    ssoAccountsActions,
    ssoAccountsDataSelector,
    ssoAccountsResultSelector,
} from '../services/sso-accounts.slice';

import type {SsoAccountsFilters, SsoAccountsSearch} from '../models/typedef';
import type {CacheKeys} from 'src/hooks/common-data-service/cached-table';
import type {DraftSortField} from 'src/openapi/admin/models/DraftSortField';
import type {SsoDraftSearchResponse} from 'src/openapi/admin/models/SsoDraftSearchResponse';

type SsoAccountsCacheKeys = CacheKeys<SsoAccountsSearch, DraftSortField, SsoAccountsFilters>;

export const useGetSsoAccounts = () => {
    const apiDispatch = useAppDispatch();
    const dispatch = useDispatch();

    const getAction = useCallback((cacheKeys: SsoAccountsCacheKeys) => apiDispatch(
        searchSsoDrafts({
            ssoDraftSearchCriteria: buildSsoDraftSearchCriteria(cacheKeys),
        }),
    ), [apiDispatch]);

    const setLoadedData = useCallback((
        response: SsoDraftSearchResponse,
        cacheKeys: SsoAccountsCacheKeys,
    ) => {
        dispatch(ssoAccountsActions.onSuccess({
            cacheKeys,
            totalCount: response.total,
            data: response.drafts,
        }));
    }, [dispatch]);

    return useCachedTable({
        dataSelector: ssoAccountsDataSelector,
        stateSelector: ssoAccountsStateSelector,
        resultSelector: ssoAccountsResultSelector,
        actions: ssoAccountsActions,
        getAction,
        onSuccessLoad: setLoadedData,
    });
};
